import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowUpDown, Info } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import AffiliateCta from './AffiliateCta';
import OccupationLink from './OccupationLink';
import { toTitleCase } from '@/lib/utils';

interface SalaryData {
  occ_title: string;
  soc: string;
  annual_mean_wage: number;
  annual_median_wage: number;
  hourly_mean_wage: number;
  total_employment: number;
  no_degree_flag?: boolean;
  // P10/P90 percentile data
  p10_salary?: number;
  p90_salary?: number;
  // Career recommendation fields
  recommendation_score?: number;
  salary_improvement_potential?: number;
  transition_difficulty?: string;
  growth_outlook_score?: number;
  skill_overlap_score?: number;
}

interface SalaryTableProps {
  data: SalaryData[];
  title?: string;
  showAffiliateEvery?: number;
  area?: string;
  className?: string;
  firstColumnLabel?: string;
  /**
   * Optional custom first-column link. Used by BestStates so state rows link
   * to `/salary/{occ}/{state}/` instead of treating the state name as an occupation.
   */
  getRowHref?: (row: SalaryData) => string | null | undefined;
}

const SalaryTable = ({ 
  data, 
  title, 
  showAffiliateEvery = 8, 
  area = '',
  className = '',
  firstColumnLabel = 'Occupation',
  getRowHref,
}: SalaryTableProps) => {
  const [sortField, setSortField] = useState<keyof SalaryData>('annual_mean_wage');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const isMobile = useIsMobile();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatMedianSalary = (row: unknown) => {
    const amount = row.annual_median_wage || row.median_salary;
    const isEstimated = row.is_median_estimated;
    
    // Handle null, undefined, or 0 median salaries
    if (amount === null || amount === undefined || amount === 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 cursor-help text-muted-foreground">
                N/A <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Median salary data not available for this industry</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <span className="inline-flex items-center gap-1">
        {formatCurrency(amount)}
        {isEstimated && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground cursor-help">*</span>
              </TooltipTrigger>
              <TooltipContent>
                <p>Estimated from mean salary (actual median not available)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
    );
  };

  const formatHourlyWage = (amount: number) => {
    // Handle null, undefined, or 0 hourly wages (common for salary-based roles)
    if (amount === null || amount === undefined || amount === 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1 cursor-help">
                N/A <Info className="h-3 w-3 text-muted-foreground" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>Hourly wage not available - typically salary-based position</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return formatCurrency(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatWageRange = (p10: number, p90: number) => {
    if (!p10 || !p90 || p10 === 0 || p90 === 0) return 'Data not available';
    return `${formatCurrency(p10)} - ${formatCurrency(p90)}`;
  };

  const formatHourlyRange = (p10: number, p90: number) => {
    if (!p10 || !p90 || p10 === 0 || p90 === 0) return 'Data not available';
    return `$${(p10 / 2080).toFixed(2)} - $${(p90 / 2080).toFixed(2)}/hr`;
  };

  const handleSort = (field: keyof SalaryData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    return sortDirection === 'asc' ? 
      aStr.localeCompare(bStr) : 
      bStr.localeCompare(aStr);
  });

  const SortButton = ({ field, children }: { field: keyof SalaryData; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className={isMobile ? "h-auto p-2 font-semibold text-xs" : "h-auto p-0 font-semibold hover:bg-transparent"}
    >
      {children}
      <ArrowUpDown className="ml-1 h-3 w-3" />
    </Button>
  );

  // Mobile Card Layout
  const MobileCardLayout = () => (
    <div className="space-y-4">
      {/* Mobile Sort Controls */}
      <div className="flex flex-wrap gap-2 p-4 bg-muted/30 rounded-lg">
        <div className="text-sm font-medium text-muted-foreground mb-2 w-full">Sort by:</div>
        <SortButton field="occ_title">{firstColumnLabel}</SortButton>
        <SortButton field="annual_mean_wage">Mean</SortButton>
        <SortButton field="annual_median_wage">Median</SortButton>
        <SortButton field="hourly_mean_wage">Hourly</SortButton>
        <SortButton field="total_employment">Jobs</SortButton>
      </div>

      {sortedData.map((row, index) => (
        <React.Fragment key={`${row.soc}-${index}`}>
          <Card className="w-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base leading-tight">
                {getRowHref?.(row) ? (
                  <Link
                    to={getRowHref(row)!}
                    className="font-semibold text-primary hover:text-primary/80 hover:underline underline-offset-4"
                  >
                    {toTitleCase(row.occ_title)}
                  </Link>
                ) : (
                  <OccupationLink 
                    occupation={{ 
                      occ_title: row.occ_title, 
                      soc: row.soc 
                    }}
                    location={area === 'United States' ? 'united-states' : area}
                    className="font-semibold hover:text-primary/80"
                  >
                    {toTitleCase(row.occ_title)}
                  </OccupationLink>
                )}
              </CardTitle>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div>
                  SOC:{' '}
                  <Link 
                    to={`/what-is-soc-code?example=${row.soc}`}
                    className="text-muted-foreground hover:text-primary transition-colors"
                    title={`Learn about SOC code ${row.soc}`}
                  >
                    {row.soc}
                  </Link>
                </div>
                {row.no_degree_flag && (
                  <div>
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-wage-secondary/20 text-wage-secondary rounded-full">
                      No Degree
                    </span>
                  </div>
                )}
                {row.recommendation_score && (
                  <div>• {row.recommendation_score}% match</div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-lg font-bold text-wage-primary">
                    {formatCurrency(row.annual_mean_wage)}
                  </div>
                  <div className="text-xs text-muted-foreground">Mean Salary</div>
                </div>
                <div className="space-y-2">
                  <div className="text-lg font-semibold">
                    {formatMedianSalary(row)}
                  </div>
                  <div className="text-xs text-muted-foreground">Median Salary</div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium">
                    {formatHourlyWage(row.hourly_mean_wage)}
                  </div>
                  <div className="text-xs text-muted-foreground">Hourly Rate</div>
                </div>
                <div className="space-y-2">
                  <div className="font-medium">
                    {formatNumber(row.total_employment)}
                  </div>
                  <div className="text-xs text-muted-foreground">Total Jobs</div>
                </div>
              </div>
              
              {row.transition_difficulty && (
                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                    row.transition_difficulty.toLowerCase() === 'easy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    row.transition_difficulty.toLowerCase() === 'moderate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    row.transition_difficulty.toLowerCase() === 'difficult' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {row.transition_difficulty}
                  </span>
                  {row.salary_improvement_potential !== undefined && (
                    <span className={`text-xs font-medium ${
                      row.salary_improvement_potential > 10 ? 'text-green-600 dark:text-green-400' :
                      row.salary_improvement_potential > 0 ? 'text-green-500 dark:text-green-300' :
                      row.salary_improvement_potential > -10 ? 'text-orange-500 dark:text-orange-400' :
                      'text-red-500 dark:text-red-400'
                    }`}>
                      {row.salary_improvement_potential >= 0 ? '+' : ''}{Math.round(row.salary_improvement_potential)}% salary
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Insert affiliate CTA every N rows for revenue optimization */}
          {(index + 1) % showAffiliateEvery === 0 && index < sortedData.length - 1 && (
            <div className="my-4">
              <AffiliateCta 
                provider="ziprecruiter"
                occupation={toTitleCase(row.occ_title)}
                location={area}
                className="w-full"
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
      )}
      
      {isMobile ? (
        <MobileCardLayout />
      ) : (
        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold">
                  <SortButton field="occ_title">{firstColumnLabel}</SortButton>
                </TableHead>
                <TableHead className="text-right font-semibold">
                  <SortButton field="annual_mean_wage">Mean Salary</SortButton>
                </TableHead>
                <TableHead className="text-right font-semibold">
                  <SortButton field="annual_median_wage">Median Salary</SortButton>
                </TableHead>
                <TableHead className="text-right font-semibold">
                  <SortButton field="hourly_mean_wage">Hourly</SortButton>
                </TableHead>
                <TableHead className="text-right font-semibold">
                  <SortButton field="total_employment">Jobs</SortButton>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((row, index) => (
                <React.Fragment key={`${row.soc}-${index}`}>
                  <TableRow className="hover:bg-muted/30">
                    <TableCell className="font-medium">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {getRowHref?.(row) ? (
                            <Link
                              to={getRowHref(row)!}
                              className="font-medium text-primary hover:text-primary/80 hover:underline underline-offset-4"
                            >
                              {toTitleCase(row.occ_title)}
                            </Link>
                          ) : (
                            <OccupationLink 
                              occupation={{ 
                                occ_title: row.occ_title, 
                                soc: row.soc 
                              }}
                              location={area === 'United States' ? 'united-states' : area}
                              className="font-medium hover:text-primary/80"
                            >
                              {toTitleCase(row.occ_title)}
                            </OccupationLink>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <div>
                            SOC:{' '}
                            <Link 
                              to={`/what-is-soc-code?example=${row.soc}`}
                              className="text-muted-foreground hover:text-primary transition-colors"
                              title={`Learn about SOC code ${row.soc}`}
                            >
                              {row.soc}
                            </Link>
                          </div>
                          {row.no_degree_flag && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-wage-secondary/20 text-wage-secondary rounded-full">
                                No Degree
                              </span>
                            </div>
                          )}
                          {row.recommendation_score && (
                            <div className="mt-0.5">• {row.recommendation_score}% match</div>
                          )}
                        </div>
                        {row.transition_difficulty && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                              row.transition_difficulty.toLowerCase() === 'easy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                              row.transition_difficulty.toLowerCase() === 'moderate' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                              row.transition_difficulty.toLowerCase() === 'difficult' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}>
                              {row.transition_difficulty}
                            </span>
                            {row.salary_improvement_potential !== undefined && (
                              <span className={`text-xs font-medium ${
                                row.salary_improvement_potential > 10 ? 'text-green-600 dark:text-green-400' :
                                row.salary_improvement_potential > 0 ? 'text-green-500 dark:text-green-300' :
                                row.salary_improvement_potential > -10 ? 'text-orange-500 dark:text-orange-400' :
                                'text-red-500 dark:text-red-400'
                              }`}>
                                {row.salary_improvement_potential >= 0 ? '+' : ''}{Math.round(row.salary_improvement_potential)}% salary
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(row.annual_mean_wage)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatMedianSalary(row)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatHourlyWage(row.hourly_mean_wage)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {formatNumber(row.total_employment)}
                    </TableCell>
                  </TableRow>
                  
                  {/* Insert affiliate CTA every N rows for revenue optimization */}
                  {(index + 1) % showAffiliateEvery === 0 && index < sortedData.length - 1 && (
                    <TableRow>
                      <TableCell colSpan={5} className="p-4 bg-muted/20">
                        <AffiliateCta 
                          provider="ziprecruiter"
                          occupation={toTitleCase(row.occ_title)}
                          location={area}
                          className="max-w-md mx-auto"
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default SalaryTable;